import axios from 'axios';

// Route-scoped request cancellation.
//
// Every request is tagged, at the moment it is issued, with the route that was
// on screen. When the user navigates to a different route we cancel the
// still-pending requests that belong to the route(s) they left, so a slow page
// can no longer spend bandwidth on — or resolve into — a screen that is gone.
//
// Tagging by route (rather than "cancel everything on navigate") is what makes
// this safe regardless of ordering: React mounts the new page and fires its
// requests *before* the navigation listener runs, but those requests are
// already tagged with the new pathname, so they are never in the cancel set.
//
// Policy: only idempotent reads (GET/HEAD) are cancelled automatically.
// Aborting a POST/PUT/PATCH/DELETE only stops the client from hearing the
// answer — the server still performs the write — so those run to completion
// unless a caller explicitly opts in. Either default can be overridden per
// request with the `abortOnNavigate` config flag:
//
//   callApi('/report', params, 'get', { abortOnNavigate: false })  // survives
//   request.post('/thing', body, { abortOnNavigate: true })        // opts in
//
// Requests made by persistent chrome (sidebar badge polling, the session
// probe) must opt out — they belong to the shell, not to any one page.

export const CANCEL_MESSAGE = 'Request aborted: navigated away from the page.';

// Pending cancellable requests: { routeKey, source }.
const pending = new Set();

// The route a request belongs to. Only the pathname counts — changing the query
// string (paging, filters) keeps you on the same page and must not cancel that
// page's own in-flight requests.
export const currentRouteKey = () =>
  typeof window !== 'undefined' && window.location
    ? window.location.pathname
    : '/';

const isAbortableByDefault = (config) => {
  const method = (config.method || 'get').toLowerCase();
  return method === 'get' || method === 'head';
};

const shouldAbortOnNavigate = (config) =>
  config.abortOnNavigate === undefined
    ? isAbortableByDefault(config)
    : !!config.abortOnNavigate;

const release = (config) => {
  const entry = config && config.__navAbortEntry;
  if (entry) pending.delete(entry);
};

// Normalised shape for a navigation cancellation. `__cancelled` is the marker
// downstream code branches on — showError ignores it, the same way it ignores
// `__approvalHandled` — so a cancelled request never raises an error toast.
const cancellation = (error) => ({
  __cancelled: true,
  message: (error && error.message) || CANCEL_MESSAGE,
});

// True for both the raw axios Cancel and the normalised rejection above.
export const isCancelledError = (err) =>
  !!(err && (err.__cancelled === true || axios.isCancel(err)));

/**
 * Wire an axios instance into the route-scoped cancellation registry.
 * Attach *after* attachAuthInterceptors so the auth interceptor keeps first
 * look at real HTTP failures and only genuine cancellations reach us.
 */
export const attachNavigationAbort = (instance) => {
  instance.interceptors.request.use((config) => {
    // A caller-supplied cancel token wins — they are managing this themselves.
    // (`__navAbortEntry` already set means this is the 2FA interceptor
    // replaying its own config, so the token on it is ours and stale: issue a
    // fresh one rather than treating it as caller-supplied.)
    if (config.cancelToken && !config.__navAbortEntry) return config;
    if (!shouldAbortOnNavigate(config)) return config;

    const source = axios.CancelToken.source();
    const entry = { routeKey: currentRouteKey(), source };
    pending.add(entry);
    config.__navAbortEntry = entry;
    config.cancelToken = source.token;
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      release(response.config);
      return response;
    },
    (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(cancellation(error));
      }
      release(error && error.config);
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Cancel every pending request that belongs to a route other than `routeKey`.
 * Called on navigation; returns the number of requests aborted.
 */
export const abortRequestsForOtherRoutes = (routeKey = currentRouteKey()) => {
  let aborted = 0;
  pending.forEach((entry) => {
    if (entry.routeKey === routeKey) return;
    // Drop it before cancelling: `source.cancel` settles the request
    // synchronously when it has not been dispatched yet.
    pending.delete(entry);
    entry.source.cancel(CANCEL_MESSAGE);
    aborted += 1;
  });
  return aborted;
};

// Test seam — the registry is module-level state shared by every instance.
export const pendingCount = () => pending.size;

export default attachNavigationAbort;
